import { InputHTMLAttributes, ReactNode } from 'react';
import LayoutGap from '@/components/Common/LayoutGap';
import { FieldValues, Path, useFormContext } from 'react-hook-form';

import FormField from '../helpers/FormField';
import FormItem from '../helpers/FormItem';
import FormLabel from '../helpers/FormLabel';
import FormDescription from '../helpers/FormDescription';
import FormMessage from '../helpers/FormMessage';
import FormControl from '../helpers/FormControl';

type CheckboxFieldProps<T extends FieldValues> = {
  label: string;
  name: Path<T>;
  description?: ReactNode;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

const CheckboxField = <T extends FieldValues>({
  label,
  description,
  name,
  className,
  ...props
}: CheckboxFieldProps<T>) => {
  const form = useFormContext<T>();

  return (
    <FormField
      name={name}
      control={form.control}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <div className="flex items-start gap-2">
            <FormControl>
              <input
                type="checkbox"
                {...field}
                {...props}
                checked={!!field.value}
                required={fieldState.error?.type === 'required'}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </FormControl>

            <LayoutGap direction="column" size="none">
              <FormLabel className="m-0">{label}</FormLabel>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </LayoutGap>
          </div>
        </FormItem>
      )}
    />
  );
};

export default CheckboxField;
